import { useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import EmailVerified from "@Components/EmailVerified";
import Layout from "@Components/Layout";
import LoginLayout from "@Components/LoginLayout";
import RedirectOnLogin from "@Components/RedirectOnLogin";
import RequireAuth from "@Components/RequireAuth";
import Applications from "@Pages/Applications";
import Application from "@Pages/Applications/Application";
import ForgotPassword from "@Pages/Auth/ForgotPassword";
import SignIn, { MfaState } from "@Pages/Auth/SignIn/SignIn";
import SignInMfa from "@Pages/Auth/SignInMfa";
import SignUp from "@Pages/Auth/SignUp";
import VerifyEmail from "@Pages/Auth/VerifyEmail";
import Dashboard from "@Pages/Dashboard";
import NotFound from "@Pages/NotFound";
import TermsAndConditions from "@Pages/TermsAndConditions";
import Tickets from "@Pages/Tickets";
import Ticket from "@Pages/Tickets/Ticket";
import Users from "@Pages/Users";
import CreateUser from "@Pages/Users/User/CreateUser";
import UpdateUser from "@Pages/Users/User/UpdateUser";

import Dealers from "@Pages/Dealers";
import Dealer from "@Pages/Dealers/Dealer";
import Roles from "@Pages/Roles";
import CreateRole from "@Pages/Roles/Role/CreateRole";
import UpdateRole from "@Pages/Roles/Role/UpdateRole";
import Teams from "@Pages/Teams";
import CreateTeam from "@Pages/Teams/Team/CreateTeam";
import UpdateTeam from "@Pages/Teams/Team/UpdateTeam";
import { CurrentUser } from "./App";
import SocketConnection from "./socket/SocketConnection";
import DealerMap from "@Pages/Dealers/DealerMap";
import HelpCenter from "@Pages/HelpCenter";
import ContactUs from "@Pages/HelpCenter/ContactUs/ContactUs";
import FrequentlyAskedQuestions from "@Pages/HelpCenter/FAQ";
import Feedback from "@Pages/HelpCenter/Feedback/Feedback";
import ReleaseNotes from "@Pages/HelpCenter/ReleaseNotes";
import BlogPostDetail from "@Pages/HelpCenter/ReleaseNotes/BlogPostDetail";
import Tutorials from "@Pages/HelpCenter/Tutorials";
import ResetPassword from "@Pages/Auth/ResetPassword";
import TitleIssues from "@Pages/TitleIssues";
import TitleIssue from "@Pages/TitleIssues/TitleIssue/TitleIssue";

const Router = ({ currentUser }: { currentUser: CurrentUser }) => {
  const [mfaState, setMfaState] = useState<MfaState>(undefined);
  return (
    <Routes>
      <Route element={<RedirectOnLogin currentUser={currentUser} />}>
        <Route element={<LoginLayout />}>
          <Route path="/sign-up" element={<SignUp />} />
          <Route
            path="/sign-in"
            element={<SignIn setMfaState={setMfaState} />}
          />

          <Route
            path="/sign-in/mfa"
            element={<SignInMfa mfaState={mfaState} />}
          />
          <Route path="/mfa-enroll" element={<>MFA</>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>
      </Route>
      <Route element={<EmailVerified currentUser={currentUser} />}>
        <Route element={<LoginLayout />}>
          <Route
            path="/sign-in/verify-email"
            element={<VerifyEmail currentUser={currentUser} />}
          />
        </Route>
      </Route>
      <Route element={<RequireAuth currentUser={currentUser} />}>
        <Route element={<TermsAndConditions />}>
          <Route element={<SocketConnection currentUser={currentUser} />}>
            <Route element={<Layout />}>
              <Route element={<Navigate to="/dashboard" />} path="/" />
              <Route element={<Dashboard />} path="/dashboard" />
              {/*  Users */}
              <Route element={<Users />} path="/users" />
              <Route element={<CreateUser />} path="/users/new" />
              <Route element={<UpdateUser />} path="/users/:id" />
              {/* Dealers */}
              <Route element={<Dealers />} path="/dealers" />
              <Route element={<Dealer />} path="/dealers/:id" />
              <Route element={<DealerMap />} path="/dealers/map" />

              {/* Deals */}
              <Route element={<Applications />} path="/loans" />
              <Route element={<Application />} path="/loans/:id" />
              {/*  Title issues */}
              <Route element={<TitleIssues />} path="/title_info" />
              <Route element={<TitleIssue />} path="/title_info/:id" />
              {/*  Tickets */}
              <Route element={<Tickets />} path="/tickets" />
              <Route element={<Ticket />} path="/tickets/:id" />

              {/* <Route element={<CreateTicket />} path="/tickets/new" /> */}
              {/* Permissions */}
              <Route element={<Roles />} path="/roles" />
              <Route element={<UpdateRole />} path="/roles/:id" />
              <Route element={<CreateRole />} path="/roles/new" />
              <Route element={<Teams />} path="/teams" />
              <Route element={<UpdateTeam />} path="/teams/:id" />
              <Route element={<CreateTeam />} path="/teams/new" />

              {/* Help center */}
              <Route element={<FrequentlyAskedQuestions />} path="/faq" />
              <Route element={<HelpCenter />} path="/help-center" />
              <Route element={<Feedback />} path="/feedback" />
              <Route element={<Tutorials />} path="/tutorials" />
              <Route element={<ReleaseNotes />} path="/release-notes" />
              <Route element={<BlogPostDetail />} path="/release-notes/:id" />
              <Route element={<ContactUs />} path="/contact-us" />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default Router;
